import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { Subject, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from "rxjs/operators";

import {
  ImtPurpose,
  ImtRegion,
  ImtSimulation,
  SimulatorsService,
} from "src/app/core/services/simulators.service";
import { priceFormatCents } from "src/app/pages/deals/deal-detail/utils/deal-detail.utils";

export type SimulatorTab = "imt" | "credit";

/** Uma linha da tabela de amortização, agregada ao ano. */
export interface AmortizationYear {
  year: number;
  interest: number;
  principal: number;
  balance: number;
}

/** O que fica guardado no browser entre aberturas do modal. */
interface SimulatorsState {
  tab: SimulatorTab;
  linked: boolean;
  imt: any;
  credit: any;
}

const STORAGE_KEY = "simulators-modal";
/** Taxa de esforço: até 35% confortável, até 50% apertada, acima disso alta. */
const EFFORT_OK = 35;
const EFFORT_WARN = 50;
/**
 * Garantia pública ao crédito jovem (DL 44/2024): até 35 anos, primeira
 * habitação própria permanente, imóvel até 450 000 €; o Estado garante até
 * 15% do financiamento e o banco pode ir aos 100%.
 */
const YOUNG_CREDIT_PRICE_LIMIT = 450000;

/**
 * Simuladores de IMT e de crédito habitação, abertos da barra superior para
 * qualquer imóvel, sem negócio por trás.
 *
 * O IMT é calculado no servidor (as tabelas vivem lá, como no processo); o
 * crédito é aritmética no cliente. "Ligar" os dois faz o crédito partir do
 * preço do IMT e somar os impostos ao capital próprio necessário — é a conta
 * que o cliente quer ouvir: quanto preciso de ter, e quanto pago por mês.
 */
@Component({
  selector: "app-simulators-modal",
  templateUrl: "./simulators-modal.component.html",
  styleUrls: ["./simulators-modal.component.scss"],
})
export class SimulatorsModalComponent implements OnInit, OnDestroy {
  tab: SimulatorTab = "imt";
  /** O crédito segue o preço do IMT e soma-lhe os impostos. */
  linked = true;

  imtForm!: FormGroup;
  creditForm!: FormGroup;

  imt: ImtSimulation | null = null;
  imtLoading = false;
  imtError = false;

  showAmortization = false;
  copied = false;

  readonly purposes: ImtPurpose[] = ["own_permanent", "own_secondary", "other"];
  readonly regions: ImtRegion[] = ["continent", "autonomous"];
  readonly downPaymentPresets = [0, 10, 20, 30];

  private readonly destroy$ = new Subject<void>();
  private copiedTimer: any = null;

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly formBuilder: FormBuilder,
    private readonly simulators: SimulatorsService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.imtForm = this.formBuilder.group({
      price: [null],
      taxableAssetValue: [null],
      purpose: ["own_permanent"],
      region: ["continent"],
      youngBuyer: [false],
    });

    this.creditForm = this.formBuilder.group({
      propertyPrice: [null],
      downPayment: [null],
      termYears: [30],
      ratePercent: [3.5],
      monthlyIncome: [null],
      youngBuyer: [false],
    });

    this.restoreState();

    // IMT: recalcula ao escrever, com um compasso de espera para não inundar
    // o servidor a cada tecla.
    this.imtForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        switchMap((value) => {
          this.persistState();
          this.syncLinkedPrice();
          const price = this.toNumber(value?.price);
          if (!(price > 0)) {
            this.imt = null;
            return of(null);
          }
          this.imtLoading = true;
          this.imtError = false;
          return this.simulators.simulateImt(value).pipe(
            catchError(() => {
              this.imtError = true;
              return of(null);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.imtLoading = false;
        if (result) this.imt = result;
      });

    this.creditForm.valueChanges
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.persistState());

    // Jovem até 35 anos é a mesma pessoa nos dois separadores: marcar num
    // marca no outro, quando estão ligados.
    this.imtForm
      .get("youngBuyer")!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((young) => {
        if (this.linked && young !== this.creditForm.value?.youngBuyer) {
          this.setCreditYoung(young === true);
        }
      });
    this.creditForm
      .get("youngBuyer")!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((young) => {
        if (
          this.linked &&
          this.isOwnPermanent &&
          young !== this.imtForm.value?.youngBuyer
        ) {
          this.imtForm.patchValue({ youngBuyer: young === true });
        }
      });

    // Um valor guardado de outra abertura arranca logo com resultado.
    if (this.toNumber(this.imtForm.value?.price) > 0) {
      this.imtForm.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ------------------------------------------------------------ Navegação

  setTab(tab: SimulatorTab): void {
    this.tab = tab;
    this.persistState();
  }

  toggleLinked(): void {
    this.linked = !this.linked;
    this.syncLinkedPrice();
    this.persistState();
  }

  /** Do IMT para o crédito, já com o preço: é o passo natural da conversa. */
  goToCreditWithPrice(): void {
    this.linked = true;
    this.syncLinkedPrice();
    this.setTab("credit");
  }

  private syncLinkedPrice(): void {
    if (!this.linked) return;
    const price = this.toNumber(this.imtForm?.value?.price);
    if (price > 0 && this.toNumber(this.creditForm?.value?.propertyPrice) !== price) {
      this.creditForm.patchValue({ propertyPrice: price }, { emitEvent: false });
      this.persistState();
    }
  }

  // ---------------------------------------------------------------- IMT

  get imtPrice(): number {
    return this.toNumber(this.imtForm?.value?.price);
  }

  get isOwnPermanent(): boolean {
    return this.imtForm?.value?.purpose === "own_permanent";
  }

  get imtEstimate() {
    return this.imt?.estimate ?? null;
  }

  /** Impostos em percentagem do preço: o número que se diz ao cliente. */
  get imtEffectiveRate(): number | null {
    const total = this.imtEstimate?.total;
    const price = this.imtPrice;
    if (!(price > 0) || total === null || total === undefined) return null;
    return Math.round((total / price) * 1000) / 10;
  }

  get acquisitionTotal(): number | null {
    const price = this.imtPrice;
    if (!(price > 0) || !this.imtEstimate) return null;
    return price + this.imtEstimate.total;
  }

  // ------------------------------------------------------------- Crédito

  get creditPrice(): number {
    return this.toNumber(this.creditForm?.value?.propertyPrice);
  }

  get downPayment(): number {
    return this.toNumber(this.creditForm?.value?.downPayment);
  }

  get downPaymentPercent(): number | null {
    const price = this.creditPrice;
    if (!(price > 0)) return null;
    return Math.round((this.downPayment / price) * 1000) / 10;
  }

  get financed(): number {
    return Math.max(0, this.creditPrice - this.downPayment);
  }

  get ltv(): number | null {
    const price = this.creditPrice;
    if (!(price > 0)) return null;
    return Math.round((this.financed / price) * 1000) / 10;
  }

  get termYears(): number {
    const years = Math.round(this.toNumber(this.creditForm?.value?.termYears));
    return Math.min(50, Math.max(1, years || 30));
  }

  get ratePercent(): number {
    const rate = this.toNumber(this.creditForm?.value?.ratePercent);
    return Math.max(0, rate);
  }

  get monthlyPayment(): number | null {
    const principal = this.financed;
    if (!(principal > 0)) return null;
    const months = this.termYears * 12;
    const rate = this.ratePercent / 100 / 12;
    if (rate === 0) return principal / months;
    return (principal * rate) / (1 - Math.pow(1 + rate, -months));
  }

  get totalPaid(): number | null {
    const payment = this.monthlyPayment;
    return payment === null ? null : payment * this.termYears * 12;
  }

  get totalInterest(): number | null {
    const total = this.totalPaid;
    return total === null ? null : total - this.financed;
  }

  get monthlyIncome(): number {
    return this.toNumber(this.creditForm?.value?.monthlyIncome);
  }

  /** Prestação sobre o rendimento do agregado. */
  get effortRate(): number | null {
    const payment = this.monthlyPayment;
    const income = this.monthlyIncome;
    if (payment === null || !(income > 0)) return null;
    return Math.round((payment / income) * 1000) / 10;
  }

  get effortClass(): string {
    const effort = this.effortRate;
    if (effort === null) return "";
    if (effort <= EFFORT_OK) return "is-ok";
    if (effort <= EFFORT_WARN) return "is-warn";
    return "is-high";
  }

  get effortLabelKey(): string {
    const effort = this.effortRate;
    if (effort === null) return "";
    if (effort <= EFFORT_OK) return "t-simulators.t-effort-ok";
    if (effort <= EFFORT_WARN) return "t-simulators.t-effort-warn";
    return "t-simulators.t-effort-high";
  }

  /** Impostos que entram na conta do capital próprio, só quando ligado. */
  get linkedTaxes(): number {
    return this.linked && this.imtEstimate ? this.imtEstimate.total : 0;
  }

  /** Entrada mais impostos: o que o cliente tem de ter de lado. */
  get ownCapital(): number | null {
    if (!(this.creditPrice > 0)) return null;
    return this.downPayment + this.linkedTaxes;
  }

  get amortization(): AmortizationYear[] {
    const payment = this.monthlyPayment;
    if (payment === null) return [];

    const rate = this.ratePercent / 100 / 12;
    let balance = this.financed;
    const rows: AmortizationYear[] = [];

    for (let year = 1; year <= this.termYears; year++) {
      let interest = 0;
      let principal = 0;
      for (let month = 0; month < 12; month++) {
        const monthInterest = balance * rate;
        const monthPrincipal = Math.min(balance, payment - monthInterest);
        interest += monthInterest;
        principal += monthPrincipal;
        balance = Math.max(0, balance - monthPrincipal);
      }
      rows.push({ year, interest, principal, balance });
      if (balance <= 0) break;
    }

    return rows;
  }

  // ---- Crédito jovem

  get creditYoungBuyer(): boolean {
    return this.creditForm?.value?.youngBuyer === true;
  }

  /** Acima do limite a garantia pública não se aplica: o ecrã avisa. */
  get youngLimitExceeded(): boolean {
    return this.creditYoungBuyer && this.creditPrice > YOUNG_CREDIT_PRICE_LIMIT;
  }

  get youngCreditPriceLimit(): number {
    return YOUNG_CREDIT_PRICE_LIMIT;
  }

  /** Marcar o crédito jovem põe a entrada a zero: é esse o ponto do regime. */
  setCreditYoung(young: boolean): void {
    this.creditForm.patchValue({
      youngBuyer: young,
      ...(young ? { downPayment: 0 } : {}),
    });
  }

  onCreditYoungChange(event: Event): void {
    this.setCreditYoung((event.target as HTMLInputElement).checked);
  }

  applyDownPaymentPreset(percent: number): void {
    const price = this.creditPrice;
    if (!(price > 0)) return;
    this.creditForm.patchValue({
      downPayment: Math.round((price * percent) / 100),
    });
  }

  isDownPaymentPreset(percent: number): boolean {
    const price = this.creditPrice;
    if (!(price > 0)) return false;
    return Math.round((price * percent) / 100) === this.downPayment;
  }

  // ------------------------------------------------------------- Resumo

  /** Resumo em texto para colar num email ou WhatsApp ao cliente. */
  copySummary(): void {
    const lines = this.tab === "imt" ? this.imtSummaryLines() : this.creditSummaryLines();
    if (!lines.length) return;

    const text = lines.join("\n");
    const done = () => {
      this.copied = true;
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => (this.copied = false), 2000);
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => this.fallbackCopy(text, done));
    } else {
      this.fallbackCopy(text, done);
    }
  }

  private fallbackCopy(text: string, done: () => void): void {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    try {
      document.execCommand("copy");
      done();
    } finally {
      document.body.removeChild(area);
    }
  }

  private imtSummaryLines(): string[] {
    const estimate = this.imtEstimate;
    if (!estimate || !(this.imtPrice > 0)) return [];
    const t = (key: string, params?: any) => this.translate.instant(key, params);
    const lines = [
      `${t("t-simulators.t-tab-imt")} (${t("t-simulators.t-tables", { year: estimate.tableYear })})`,
      `${t("t-simulators.t-price")}: ${this.money(this.imtPrice)}`,
      `${t("t-simulators.t-purpose")}: ${t("t-simulators.t-purpose-" + estimate.purpose)}`,
      `${t("t-simulators.t-region")}: ${t("t-simulators.t-region-" + estimate.region)}`,
    ];
    if (estimate.youngBuyer) lines.push(t("t-simulators.t-young-buyer"));
    const stampDutyNote = estimate.stampDutyExempt
      ? ` (${t("t-simulators.t-exempt-young")})`
      : estimate.stampDutyPartialExempt
        ? ` (${t("t-simulators.t-partial-exempt-young")})`
        : "";
    lines.push(
      `${t("t-simulators.t-imt")}: ${this.money(estimate.imt)}`,
      `${t("t-simulators.t-stamp-duty")}: ${this.money(estimate.stampDuty)}${stampDutyNote}`,
      `${t("t-simulators.t-taxes-total")}: ${this.money(estimate.total)}`,
      `${t("t-simulators.t-acquisition-total")}: ${this.money(this.acquisitionTotal)}`,
      "",
      t("t-simulators.t-disclaimer-imt"),
    );
    return lines;
  }

  private creditSummaryLines(): string[] {
    const payment = this.monthlyPayment;
    if (payment === null) return [];
    const t = (key: string, params?: any) => this.translate.instant(key, params);
    const lines = [
      t("t-simulators.t-tab-credit"),
      `${t("t-simulators.t-property-price")}: ${this.money(this.creditPrice)}`,
      `${t("t-simulators.t-down-payment")}: ${this.money(this.downPayment)} (${this.downPaymentPercent}%)`,
      `${t("t-simulators.t-financed")}: ${this.money(this.financed)} (LTV ${this.ltv}%)`,
      `${t("t-simulators.t-term")}: ${this.termYears} ${t("t-simulators.t-years")}`,
      `${t("t-simulators.t-rate")}: ${this.ratePercent}%`,
      `${t("t-simulators.t-monthly-payment")}: ${this.money(payment)}`,
      `${t("t-simulators.t-total-interest")}: ${this.money(this.totalInterest)}`,
      `${t("t-simulators.t-total-paid")}: ${this.money(this.totalPaid)}`,
    ];
    if (this.creditYoungBuyer) {
      lines.push(t("t-simulators.t-young-credit"));
    }
    if (this.effortRate !== null) {
      lines.push(`${t("t-simulators.t-effort-rate")}: ${this.effortRate}%`);
    }
    if (this.ownCapital !== null) {
      lines.push(`${t("t-simulators.t-own-capital")}: ${this.money(this.ownCapital)}`);
    }
    lines.push("", t("t-simulators.t-disclaimer-credit"));
    return lines;
  }

  // -------------------------------------------------------------- Estado

  private persistState(): void {
    try {
      const state: SimulatorsState = {
        tab: this.tab,
        linked: this.linked,
        imt: this.imtForm?.value ?? null,
        credit: this.creditForm?.value ?? null,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Navegação privada ou sem espaço: o modal funciona na mesma.
    }
  }

  private restoreState(): void {
    let state: SimulatorsState | null = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      state = raw ? JSON.parse(raw) : null;
    } catch {
      return;
    }
    if (!state) return;

    if (state.tab === "imt" || state.tab === "credit") this.tab = state.tab;
    if (typeof state.linked === "boolean") this.linked = state.linked;
    if (state.imt) this.imtForm.patchValue(state.imt, { emitEvent: false });
    if (state.credit) this.creditForm.patchValue(state.credit, { emitEvent: false });
  }

  // --------------------------------------------------------------- Utils

  money(value: any): string {
    return priceFormatCents(value, "€") ?? "—";
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = Number(`${value}`.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
